import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Card } from 'ui-components/components/card';
import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { GuidService } from 'ui-components/services/guid-service';

import { BLOCKING_CONFIG } from '../../../../../constants/fraud-constants';
import { messages } from './messages';
import styles from './styles.module.less';

import type { BlockingBehavior } from '../../../../../constants/fraud-constants';
import type { ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';

type LossPreventionCardProps = {
  title: MessageDescriptor;
  description: MessageDescriptor;
  blockingBehavior: BlockingBehavior;
  isCardDisabled: boolean;
  onToggle: (enabled: boolean) => void;
  children?: ReactNode;
  linkButton?: {
    text: string;
    onClick: () => void;
  };
};

export const LossPreventionCard = ({
  title,
  description,
  blockingBehavior,
  isCardDisabled,
  onToggle,
  children,
  linkButton,
}: LossPreventionCardProps) => {
  const intl = useIntl();
  const checkboxId = useMemo(() => GuidService.newGuid(), []);
  const blockingConfig = BLOCKING_CONFIG[blockingBehavior] ?? BLOCKING_CONFIG.info;

  return (
    <Card>
      <FlexRow className={styles['card-header']}>
        <FlexRow className={styles['title-section']}>
          <Text className={styles['card-title']} textType={TextType.H4} value={intl.formatMessage(title)} />
          <span
            className={classNames(styles['blocking-badge'], styles[blockingConfig.className], {
              [styles['badge-hidden']]: isCardDisabled,
            })}
          >
            <FontAwesomeIcon className={styles['blocking-icon']} icon={blockingConfig.icon} />
            <Text textType={TextType.Caption} value={messages[blockingConfig.messageKey]} />
          </span>
        </FlexRow>
        <CheckboxSlider
          checkboxId={checkboxId}
          checked={!isCardDisabled}
          labelText={intl.formatMessage(!isCardDisabled ? messages.toggleOn : messages.toggleOff)}
          onChange={onToggle}
        />
      </FlexRow>

      <Text className={styles['card-description']} textType={TextType.Description} value={intl.formatMessage(description)} />
      {children ? (
        <div
          className={classNames(styles['settings-section'], {
            [styles['settings-disabled']]: isCardDisabled,
          })}
        >
          {children}
        </div>
      ) : null}
      {linkButton ? (
        <div className={styles['link-button-section']}>
          <Button onClick={linkButton.onClick} theme={THEME_CONSTANTS.link}>
            {linkButton.text}
          </Button>
        </div>
      ) : null}
    </Card>
  );
};
